/** Go.Do Local Engine client (localhost HTTP). */
export const LOCAL_ENGINE_DEFAULT_URL =
  (import.meta.env.VITE_LOCAL_ENGINE_URL as string | undefined)?.trim() || "http://127.0.0.1:4000";

const LOCAL_ENGINE_URL_KEY = "godo_local_engine_base_url";
const LOCAL_ENGINE_SECRET_KEY = "godo_local_engine_secret";

export function getStoredLocalEngineSecret(): string | null {
  try {
    return sessionStorage.getItem(LOCAL_ENGINE_SECRET_KEY);
  } catch {
    return null;
  }
}

export function setStoredLocalEngineSecret(secret: string): void {
  try {
    sessionStorage.setItem(LOCAL_ENGINE_SECRET_KEY, secret);
  } catch {
    /* ignore */
  }
}

export function clearStoredLocalEngineSecret(): void {
  try {
    sessionStorage.removeItem(LOCAL_ENGINE_SECRET_KEY);
  } catch {
    /* ignore */
  }
}

/** Headers for authenticated Local Engine HTTP (after /v1/config pairing). */
export function localEngineAuthHeaders(): Record<string, string> {
  const secret = getStoredLocalEngineSecret();
  if (!secret) return {};
  return { "X-Local-Engine-Secret": secret };
}
const PORT_SCAN_FIRST = 4000;
const PORT_SCAN_COUNT = 20;
const SCAN_COOLDOWN_KEY = "godo_local_engine_scan_cooldown_until";
const SCAN_COOLDOWN_MS = 90_000;

export function clearLocalEngineScanCooldown(): void {
  try {
    sessionStorage.removeItem(SCAN_COOLDOWN_KEY);
  } catch {
    /* ignore */
  }
}

function scanCooldownActive(): boolean {
  try {
    const until = Number(sessionStorage.getItem(SCAN_COOLDOWN_KEY) || 0);
    return Date.now() < until;
  } catch {
    return false;
  }
}

function markScanCooldown(): void {
  try {
    sessionStorage.setItem(SCAN_COOLDOWN_KEY, String(Date.now() + SCAN_COOLDOWN_MS));
  } catch {
    /* ignore */
  }
}

/** Finds helper on 4000–4019 (or cached port). Skips full scan during cooldown unless `force`. */
export async function discoverLocalEngineBaseUrl(opts?: { force?: boolean }): Promise<string> {
  const cached = sessionStorage.getItem(LOCAL_ENGINE_URL_KEY);
  if (cached) {
    const h = await pingLocalEngine(cached);
    if (h) {
      clearLocalEngineScanCooldown();
      return cached;
    }
    sessionStorage.removeItem(LOCAL_ENGINE_URL_KEY);
  }

  if (!opts?.force && scanCooldownActive()) {
    return cached || LOCAL_ENGINE_DEFAULT_URL;
  }

  const ports = Array.from({ length: PORT_SCAN_COUNT }, (_, i) => PORT_SCAN_FIRST + i);
  const hits = await Promise.all(
    ports.map(async (port) => {
      const url = `http://127.0.0.1:${port}`;
      const h = await pingLocalEngine(url);
      return h ? url : null;
    }),
  );
  const found = hits.find((u): u is string => u != null);
  if (found) {
    sessionStorage.setItem(LOCAL_ENGINE_URL_KEY, found);
    clearLocalEngineScanCooldown();
    return found;
  }

  markScanCooldown();
  return LOCAL_ENGINE_DEFAULT_URL;
}

export const LOCAL_ENGINE_VERSION = "1.0.0";

export type LocalEngineHealth = {
  ok: boolean;
  service: string;
  version: string;
  uptimeSec: number;
  machine?: string;
};

export type LocalEngineStatus = {
  ok: boolean;
  sqlite: boolean;
  pendingPos: number;
  syncOutbox: number;
  openImportJobs: number;
  apiConfigured: boolean;
  tokenConfigured: boolean;
  zkteco?: {
    paired: boolean;
    serverUrl: string | null;
    loopRunning: boolean;
  };
};

export type HardwareFeature =
  | "serial_scale"
  | "tax_token"
  | "thermal_print"
  | "zkteco_lan_scan"
  | "local_excel_import"
  | "pos_offline";

export type DeviceClass = "mobile" | "tablet" | "desktop";

export function detectDeviceClass(): DeviceClass {
  if (typeof navigator === "undefined") return "desktop";
  const ua = navigator.userAgent;
  const isTablet = /iPad|Tablet|PlayBook|Silk/i.test(ua) || (navigator.maxTouchPoints > 1 && /MacIntel/.test(ua));
  const isMobile = /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
  if (isTablet) return "tablet";
  if (isMobile) return "mobile";
  return "desktop";
}

export function hardwareRequiresLocalEngine(feature: HardwareFeature): boolean {
  return [
    "serial_scale",
    "tax_token",
    "thermal_print",
    "zkteco_lan_scan",
    "local_excel_import",
    "pos_offline",
  ].includes(feature);
}

export function hardwareBlockedOnMobile(feature: HardwareFeature): boolean {
  return hardwareRequiresLocalEngine(feature);
}

const HARDWARE_LABELS: Record<HardwareFeature, { ar: string; en: string }> = {
  serial_scale: { ar: "قراءة الميزان (Serial/COM)", en: "Serial scale / COM port" },
  tax_token: { ar: "توكن الضريبة (Serial)", en: "Tax token (serial)" },
  thermal_print: { ar: "الطباعة الحرارية المباشرة", en: "Direct thermal printing" },
  zkteco_lan_scan: { ar: "مسح شبكة ZKTeco", en: "ZKTeco LAN scan" },
  local_excel_import: { ar: "تنظيف استيراد Excel محلياً", en: "Local Excel import grid" },
  pos_offline: { ar: "نقطة البيع دون اتصال", en: "Offline POS queue" },
};

export function hardwareFeatureLabel(feature: HardwareFeature, lang: "ar" | "en" = "ar"): string {
  return HARDWARE_LABELS[feature][lang];
}

async function localFetch<T>(baseUrl: string, path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${baseUrl.replace(/\/$/, "")}${path}`, {
    ...init,
    headers: {
      Accept: "application/json",
      ...localEngineAuthHeaders(),
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `Local Engine ${res.status}`);
  }
  return res.json() as Promise<T>;
}

export async function pingLocalEngine(baseUrl = LOCAL_ENGINE_DEFAULT_URL): Promise<LocalEngineHealth | null> {
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 2500);
    const data = await localFetch<LocalEngineHealth>(baseUrl, "/v1/health", { signal: ctrl.signal });
    clearTimeout(t);
    return data.ok ? data : null;
  } catch {
    return null;
  }
}

export async function fetchLocalEngineStatus(baseUrl = LOCAL_ENGINE_DEFAULT_URL) {
  return localFetch<LocalEngineStatus>(baseUrl, "/v1/status");
}

export async function configureLocalEngine(
  config: {
    apiBaseUrl?: string;
    accessToken?: string;
    connectorToken?: string;
    orgId?: string;
    machineLabel?: string;
    pairingCode?: string;
  },
  baseUrl = LOCAL_ENGINE_DEFAULT_URL,
) {
  const result = await localFetch<{ ok: boolean; localSecret?: string }>(baseUrl, "/v1/config", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(config),
  });
  if (result.localSecret) {
    setStoredLocalEngineSecret(result.localSecret);
  }
  return result;
}

export async function triggerLocalSync(baseUrl = LOCAL_ENGINE_DEFAULT_URL) {
  return localFetch<{ ok: boolean }>(baseUrl, "/v1/sync/run", { method: "POST" });
}

export async function scanZktecoLanLocal(baseUrl = LOCAL_ENGINE_DEFAULT_URL) {
  return localFetch<{ ok: boolean; devices: unknown[] }>(baseUrl, "/v1/zkteco/lan-scan");
}

/** Unique provisional invoice number per machine + user (offline POS). */
export async function fetchOfflinePosInvoiceNo(
  orgId: string,
  userId: string,
  baseUrl = LOCAL_ENGINE_DEFAULT_URL,
) {
  const q = new URLSearchParams({ orgId, userId });
  return localFetch<{ ok: boolean; invoiceNo: string; provisional: boolean }>(
    baseUrl,
    `/v1/serial/pos-invoice?${q}`,
  );
}
